import React, { useState, useEffect } from 'react';
import { FaEye, FaEyeSlash } from 'react-icons/fa';
import { useAuth } from '../../context/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import './signin.css';

const Signin = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, forgotPassword } = useAuth();

  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [message, setMessage] = useState({ text: '', isSuccess: false });
  const [showLoginPassword, setShowLoginPassword] = useState(false);
  const [forgotPasswordMode, setForgotPasswordMode] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [loading, setLoading] = useState(false);

  // Clear any session-related query params from URL
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const expirationType = queryParams.get('session');
    
    if (expirationType) {
      const cleanUrl = window.location.pathname;
      window.history.replaceState({}, document.title, cleanUrl);
    }
  }, [location]);

  const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const isValidPassword = (password) => password.length >= 8;

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ text: '', isSuccess: false });
    
    const trimmedEmail = loginEmail.trim();
    const trimmedPassword = loginPassword.trim();
  
    // Client-side validation
    if (!trimmedEmail || !trimmedPassword) {
      setMessage({ text: 'Please fill in all fields.', isSuccess: false });
      setLoading(false);
      return;
    }
  
    if (!isValidEmail(trimmedEmail)) {
      setMessage({ text: 'Please enter a valid email address', isSuccess: false });
      setLoading(false);
      return;
    }
  
    try {
      const result = await login(trimmedEmail, trimmedPassword);
      if (!result.success) {
        setMessage({ 
          text: result.message, 
          isSuccess: false 
        });
        
      }
    } catch (error) {
      
      // internal error handling.
      console.error('Unexpected login error:', error);
      setMessage({ 
        text: 'An unexpected error occurred. Please try again.', 
        isSuccess: false 
      });
    } finally {
      setLoading(false);
    }
  };
    

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setMessage({ text: '', isSuccess: false });
    const trimmedEmail = loginEmail.trim();

    if (!trimmedEmail) {
      setMessage({ text: 'Please enter your email.', isSuccess: false });
      return;
    }

    if (!isValidEmail(trimmedEmail)) {
      setMessage({ text: 'Please enter a valid email address', isSuccess: false });
      return;
    }

    setLoading(true);
    try {
      const result = await forgotPassword(trimmedEmail);
      setMessage({ 
        text: result.message, 
        isSuccess: result.success 
      });
      
      // Auto-clear success message after 5 seconds
      if (result.success) {
        setTimeout(() => {
          setMessage(prev => prev.isSuccess ? { text: '', isSuccess: false } : prev);
        }, 5000);
      }
    } catch (error) {
      
      let errorMessage = 'An unexpected error occurred. Please try again.';
      
      if (error.isNetworkError) { 
        errorMessage = 'Cannot connect to the server. The server might be offline or your network connection is down. Please try again later.';
      } else if (error.response) {
        if (error.response.status === 500) {
          errorMessage = 'Server error. Please try again later.';
        } else if (error.response.data?.message) {
          errorMessage = error.response.data.message;
        }
      }
      
      
      setMessage({ 
        text: errorMessage, 
        isSuccess: false 
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container">
      {forgotPasswordMode ? (
        <div className="forgot-password-form">
          <h1>Reset Password</h1>
          <form onSubmit={handleForgotPassword}>
            <input
              type="email"
              placeholder="Enter your email"
              value={loginEmail}
              onChange={(e) => setLoginEmail(e.target.value)}
              className={message.isEmailError ? 'input-error' : ''}
            />
            <input 
              type="submit" 
              value={loading ? 'Sending...' : 'Send Reset Link'} 
              disabled={loading}
            />
          </form>
          <small 
            onClick={() => setForgotPasswordMode(false)} 
            className="back-to-login"
          >
            Back to Sign In
          </small>
          {message.text && (
            <p className={`${message.isSuccess ? 'message-success' : 'message-error'}`}>
              {message.text}
            </p>
          )}
        </div>
      ) : (
        <div className="signin">
          <h1>Sign In</h1>
          <form onSubmit={handleLogin}>
            <input
              type="email"
              placeholder="Company Email"
              value={loginEmail}
              onChange={(e) => setLoginEmail(e.target.value)}
              className={message.isEmailError ? 'input-error' : ''}
            />
            <div className="password-input">
              <input
                type={showLoginPassword ? 'text' : 'password'}
                placeholder="Password"
                value={loginPassword}
                onChange={(e) => setLoginPassword(e.target.value)}
              />
              <span
                className="password-toggle-icon"
                onClick={() => setShowLoginPassword(!showLoginPassword)}
                tabIndex="0"
                aria-label={showLoginPassword ? 'Hide password' : 'Show password'}
              >
                {showLoginPassword ? <FaEyeSlash /> : <FaEye />}
              </span>
            </div>
            <div className="remember-me-container">
              <input
                type="checkbox"
                id="remember-me" 
                className="remember-me"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
              />
              <label htmlFor="remember-me">Remember Me</label>
            </div>
            <input 
              type="submit" 
              value={loading ? 'Logging in...' : 'Login'} 
              disabled={loading} 
            />
            {loading && <div className="loading-spinner"></div>}
            <small 
              onClick={() => setForgotPasswordMode(true)} 
              className="forgot-password"
            >
              Forgot Password?
            </small>
            {message.text && (
              <p className={`${message.isSuccess ? 'message-success' : 'message-error'}`}>
                {message.text}
              </p>
            )}
          </form>
          <small>
            Don't have an account?{' '}
            <span 
              onClick={() => navigate('/signup')} 
              className="toggle-link"
              tabIndex="0"
              role="button"
            >
              Sign Up
            </span>
          </small>
        </div>
      )}
    </div>
  );
};

export default Signin;